import { ReportDefinition, ReportTypes } from "./types";

export const productDiscountSalesDefinition: ReportDefinition = {
    translationKey: "report.product_discount_sales.name",
    reportType: ReportTypes.PRODUCT_DISCOUNT_SALES,
    primaryTable: "rm_invoice",
    joins: {
        cash_register: {
            sql: "LEFT JOIN cash_register ON cash_register.id = rm_invoice.cash_register_id",
        },
        department: {
            sql: "LEFT JOIN department ON department.id = rm_invoice.department_id",
        },
        invoice_item: {
            sql: "LEFT JOIN rm_invoice_item ON rm_invoice_item.invoice_id = rm_invoice.id",
        },
        product: {
            sql: "LEFT JOIN product ON product.id = rm_invoice_item.product_id",
            dependOn: ["invoice_item"],
        },
        product_group: {
            sql: "LEFT JOIN product_group ON product_group.id = rm_invoice_item.product_group_id",
            dependOn: ["invoice_item"],
        },
        invoice_item_discount: {
            sql: "JOIN rm_invoice_item_discount ON rm_invoice_item_discount.invoice_item_id = rm_invoice_item.id",
        },
        user: {
            sql: 'JOIN "user" ON "user".id = rm_invoice.user_id',
        },
    },
    fields: {
        report_date: {
            translationKey: "report.columns.report_date",
            type: "CUSTOM",
            content:
                "TO_CHAR(rm_invoice.created_at::TIMESTAMPTZ AT TIME ZONE {{time_zone}}, 'YYYY-MM-DD')",
            selectedByDefault: false,
            formatAs: "DATE",
        },
        department_name: {
            translationKey: "report.columns.department_name",
            type: "SIMPLE",
            field: { table: "department", column: "name" },
            selectedByDefault: true,
            dependOnJoin: ["department"],
            default: "No department",
        },
        cash_register: {
            translationKey: "report.columns.cash_register",
            type: "CUSTOM",
            content:
                "COALESCE(rm_invoice.cash_register_name, cash_register.name, 'No cash register')",
            selectedByDefault: false,
            dependOnJoin: ["cash_register"],
        },
        product_group_name: {
            translationKey: "report.columns.product_group_name",
            type: "SIMPLE",
            field: { table: "product_group", column: "name" },
            selectedByDefault: true,
            dependOnJoin: ["product_group"],
            default: "No product group",
        },
        product_name: {
            translationKey: "report.columns.product_name",
            type: "CUSTOM",
            content:
                "COALESCE(rm_invoice_item.name, product.name, 'No product')",
            selectedByDefault: true,
            dependOnJoin: ["product", "invoice_item"],
        },
        discount_line_description: {
            translationKey: "report.columns.discount_line_description",
            type: "SIMPLE",
            field: {
                table: "rm_invoice_item_discount",
                column: "line_description",
            },
            selectedByDefault: true,
            dependOnJoin: ["invoice_item_discount"],
        },
        discount_type: {
            translationKey: "report.columns.discount_type",
            type: "SIMPLE",
            field: {
                table: "rm_invoice_item_discount",
                column: "discount_type",
            },
            selectedByDefault: true,
            dependOnJoin: ["invoice_item_discount"],
        },
        discount_value_type: {
            translationKey: "report.columns.discount_value_type",
            type: "SIMPLE",
            field: { table: "rm_invoice_item_discount", column: "value_type" },
            selectedByDefault: true,
            dependOnJoin: ["invoice_item_discount"],
        },
        // discount_value: {
        //     translationKey: "report.columns.discount_value",
        //     type: "SUM_AMOUNT",
        //     field: { table: "rm_invoice_item_discount", column: "amount" },
        //     selectedByDefault: true,
        //     dependOnJoin: ["invoice_item_discount"],
        //     formatAs: "AMOUNT",
        // },
        discount_manual: {
            translationKey: "report.columns.discount_manual",
            type: "CUSTOM",
            content:
                "CASE WHEN rm_invoice_item_discount.manual THEN 'Yes' ELSE 'No' END",
            selectedByDefault: false,
            dependOnJoin: ["invoice_item_discount"],
        },
        discount_is_template: {
            translationKey: "report.columns.discount_is_template",
            type: "CUSTOM",
            content:
                "CASE WHEN rm_invoice_item_discount.is_template THEN 'Yes' ELSE 'No' END",
            selectedByDefault: true,
            dependOnJoin: ["invoice_item_discount"],
        },
        user_name: {
            translationKey: "report.columns.user_name",
            type: "SIMPLE",
            field: { table: "user", column: "username" },
            selectedByDefault: false,
            dependOnJoin: ["user"],
        },
        quantity: {
            translationKey: "report.columns.quantity",
            type: "SUM_FLOAT",
            field: { table: "rm_invoice_item", column: "quantity" },
            selectedByDefault: true,
            dependOnJoin: ["invoice_item"],
            formatAs: "QUANTITY",
        },
        amount: {
            translationKey: "report.columns.amount",
            type: "SUM_AMOUNT",
            field: { table: "rm_invoice_item", column: "amount" },
            selectedByDefault: true,
            dependOnJoin: ["invoice_item"],
            formatAs: "AMOUNT",
        },
        discount_amount: {
            translationKey: "report.columns.discount_amount",
            type: "SUM_AMOUNT",
            field: { table: "rm_invoice_item", column: "discount_amount" },
            selectedByDefault: true,
            dependOnJoin: ["invoice_item"],
            formatAs: "AMOUNT",
        },
    },
    filters: {
        cash_register: {
            field: { table: "cash_register", column: "id" },
            translationKey: "report.columns.cash_register",
            type: "IN",
            inType: "UUID",
            dependOnJoin: ["cash_register"],
        },
        department: {
            field: { table: "department", column: "id" },
            translationKey: "report.columns.department",
            type: "IN",
            inType: "UUID",
            dependOnJoin: ["department"],
        },
        user: {
            field: { table: "user", column: "id" },
            translationKey: "report.columns.user_name",
            type: "IN",
            inType: "UUID",
            dependOnJoin: ["user"],
        },
        merchant_id: {
            field: { table: "rm_invoice", column: "merchant_id" },
            translationKey: "report.columns.merchant_id",
            type: "EQUALS",
            internalUse: true,
        },
        report_date: {
            field: {
                table: "rm_invoice",
                column: "created_at",
            },
            translationKey: "report.columns.created_at",
            type: "BETWEEN",
        },
        product_group: {
            field: { table: "product_group", column: "id" },
            translationKey: "report.columns.product_group_name",
            type: "IN",
            inType: "UUID",
            dependOnJoin: ["product_group"],
        },
        product: {
            field: { table: "product", column: "id" },
            translationKey: "report.columns.product_name",
            type: "IN",
            inType: "UUID",
            dependOnJoin: ["product"],
        },
        discount_type: {
            field: {
                table: "rm_invoice_item_discount",
                column: "discount_type",
            },
            translationKey: "report.columns.discount_type",
            type: "EQUALS",
            dependOnJoin: ["invoice_item_discount"],
        },
        discount_is_template: {
            field: { table: "rm_invoice_item_discount", column: "is_template" },
            translationKey: "report.columns.discount_is_template",
            type: "EQUALS",
            dependOnJoin: ["invoice_item_discount"],
        },
        discount_manual: {
            field: { table: "rm_invoice_item_discount", column: "manual" },
            translationKey: "report.columns.discount_manual",
            type: "EQUALS",
            dependOnJoin: ["invoice_item_discount"],
        },
    },
    grouping: {
        department: {
            translationKey: "report.columns.department",
            type: "DEPARTMENT",
            dependOnJoin: ["department"],
        },
        report_date: {
            translationKey: "report.columns.report_date",
            type: "LABEL",
        },
        product_name: {
            translationKey: "report.columns.product_name",
            type: "LABEL",
            internalUse: true,
        },
        product_group_name: {
            translationKey: "report.columns.product_group_name",
            type: "LABEL",
            internalUse: true,
        },
        discount_type: {
            translationKey: "report.columns.discount_type",
            type: "LABEL",
            dependOnJoin: ["invoice_item_discount"],
        },
        discount_line_description: {
            translationKey: "report.columns.discount_line_description",
            type: "LABEL",
            dependOnJoin: ["invoice_item_discount"],
        },
        discount_is_template: {
            translationKey: "report.columns.discount_is_template",
            type: "LABEL",
            dependOnJoin: ["invoice_item_discount"],
        },
        user_name: {
            translationKey: "report.columns.user_name",
            type: "USER",
            dependOnJoin: ["user"],
        },
        year: {
            translationKey: "report.columns.year",
            type: "YEAR",
            field: { table: "rm_invoice", column: "created_at" },
        },
        month: {
            translationKey: "report.columns.month",
            type: "MONTH",
            field: { table: "rm_invoice", column: "created_at" },
        },
        day: {
            translationKey: "report.columns.day",
            type: "DAY",
            field: { table: "rm_invoice", column: "created_at" },
        },
        hour: {
            translationKey: "report.columns.hour",
            type: "HOUR",
            field: { table: "rm_invoice", column: "created_at" },
        },
    },
    ordering: {
        report_date: {
            translationKey: "report.columns.report_date",
            type: "CUSTOM",
            content: "report_date",
        },
        department_name: {
            translationKey: "report.columns.department_name",
            type: "CUSTOM",
            content: "department_name",
        },
        product_name: {
            translationKey: "report.columns.product_name",
            type: "CUSTOM",
            content: "product_name",
        },
        product_group_name: {
            translationKey: "report.columns.product_group_name",
            type: "CUSTOM",
            content: "product_group_name",
        },
    },
};
